<template>
  <div class="session-form-modal">
    <div class="session-form-container">
      <h2>{{ mode === 'add' ? 'Add New Session' : 'Edit Session' }}</h2>
      <form @submit.prevent="onSubmit">
        <div class="form-group">
          <label for="session-date">Session Date</label>
          <input
            id="session-date"
            type="date"
            v-model="formData.session_date"
            required
          />
        </div>
        <div class="form-group">
          <label for="session-location">Location</label>
          <input
            id="session-location"
            type="text"
            v-model="formData.location"
            required
            maxlength="100"
          />
        </div>
        <div class="form-group">
          <label for="session-notes">Session Notes (Optional)</label>
          <textarea
            id="session-notes"
            v-model="formData.notes"
            rows="4"
            placeholder="Add notes about this session..."
          ></textarea>
        </div>
        <div class="form-group">
          <label for="admin-password">Admin Password</label>
          <input
            id="admin-password"
            type="password"
            v-model="localPassword"
            required
            autocomplete="current-password"
          />
        </div>
        <div v-if="formError" class="form-error">{{ formError }}</div>
        <div class="form-actions">
          <button type="submit" :disabled="isLoading">{{ isLoading ? 'Saving...' : (mode === 'add' ? 'Add Session' : 'Save Changes') }}</button>
          <button type="button" @click="$emit('cancel')" :disabled="isLoading">Cancel</button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue';
import { defineProps, defineEmits } from 'vue';
const props = defineProps({
  initialData: {
    type: Object,
    default: null
  },
  mode: {
    type: String,
    required: true
  },
  adminPassword: {
    type: String,
    default: ''
  },
  isLoading: {
    type: Boolean,
    default: false
  }
});
const emit = defineEmits(['submit', 'cancel']);

function toDateInputValue(dateString) {
  if (!dateString) return '';
  // Extract YYYY-MM-DD part from potential ISO string (YYYY-MM-DDTHH:mm:ss.sssZ) or YYYY-MM-DD
  const datePartMatch = dateString.match(/^(\d{4}-\d{2}-\d{2})/);
  if (datePartMatch && datePartMatch[1]) {
    return datePartMatch[1];
  }
  // Fallback for invalid date strings, though input type=date expects YYYY-MM-DD
  return '';
}

const formData = ref({
  session_date: props.initialData && props.initialData.session_date ? toDateInputValue(props.initialData.session_date) : '',
  location: props.initialData && props.initialData.location ? props.initialData.location : '',
  notes: props.initialData && props.initialData.notes ? props.initialData.notes : ''
});
const localPassword = ref(props.adminPassword || '');
const formError = ref('');

watch(() => props.initialData, (val) => {
  if (val) {
    formData.value = {
      session_date: toDateInputValue(val.session_date),
      location: val.location || '',
      notes: val.notes || ''
    };
  } else {
    formData.value = { session_date: '', location: '', notes: '' };
  }
  formError.value = '';
});

function validate() {
  if (!formData.value.session_date) {
    formError.value = 'Session date is required.';
    return false;
  }
  if (!formData.value.location) {
    formError.value = 'Location is required.';
    return false;
  }
  if (!localPassword.value) {
    formError.value = 'Admin password is required.';
    return false;
  }
  return true;
}

function onSubmit() {
  if (!validate()) return;
  // Always send session_date as YYYY-MM-DD
  const cleanSessionDate = toDateInputValue(formData.value.session_date);
  emit('submit', { ...formData.value, session_date: cleanSessionDate, password: localPassword.value });
}
</script>

<style scoped>
.session-form-modal {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.session-form-container {
  background: #232323;
  padding: 2rem 2.5rem;
  border-radius: 8px;
  min-width: 320px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.3);
}
h2 {
  margin-bottom: 1.2rem;
}
.form-group {
  margin-bottom: 1rem;
  display: flex;
  flex-direction: column;
}
label {
  margin-bottom: 0.3rem;
  font-weight: 500;
}
input[type="text"],
input[type="date"],
input[type="password"],
textarea {
  padding: 0.5rem;
  border-radius: 4px;
  border: 1px solid #444;
  background: #181818;
  color: #fff;
  font-family: inherit;
}
textarea {
  resize: vertical;
  min-height: 80px;
}
.form-actions {
  display: flex;
  gap: 1rem;
  margin-top: 1.2rem;
}
button {
  padding: 0.5rem 1.2rem;
  border: none;
  border-radius: 4px;
  background: #2d8cf0;
  color: #fff;
  font-weight: bold;
  cursor: pointer;
}
button[disabled] {
  opacity: 0.7;
  cursor: not-allowed;
}
.form-error {
  color: #ff5252;
  margin-bottom: 0.7rem;
  font-size: 0.98em;
}
</style> 